import { CubeProvider } from '@cubejs-client/react';
import { CacheProvider } from '@emotion/react';
import { LicenseInfo } from '@mui/x-license';
import { getAnalyticsInstance } from '@procureai/analytics';
import { CuriosityProvider } from '@procureai/curiosity-ui-mui';
import { PrintProvider } from '@procureai/ui-mui';
import { ConfirmationDialogProvider, FetchClientProvider, FileStorageProvider } from '@procureai/uix';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { AgGridProvider } from 'ag-grid-react';
import { SnackbarProvider } from 'notistack';
import { compose } from 'ramda';
import { PropsWithChildren, ReactNode, useMemo } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { I18nextProvider } from 'react-i18next';
import { AuthProvider } from 'react-oidc-context';
import { AnalyticsProvider } from 'use-analytics';

import { FeatureFlagProvider } from '../FeatureFlagProvider/FeatureFlagProvider';
import { appConfig } from '~/common/config';
import { i18n } from '~/common/i18n';
import { preserveLocationSearchParams } from '~/common/types/LocationSearchParams';
import { ApolloAuthProvider } from '~/components/ApolloAuthProvider/ApolloAuthProvider';
import { BrowserRouterPreserveParams as Router } from '~/components/BrowserRouterPreserveParams/BrowserRouterPreserveParams';
import { ConfigProvider } from '~/components/ConfigProvider';
import { IntercomProviderConfigurable } from '~/components/IntercomProviderConfigurable/IntercomProviderConfigurable';
import { ModulePageConfigProvider } from '~/components/ModulePageConfigProvider';
import { PermissionsProvider } from '~/components/Permissions';
import { RuntimeProvider } from '~/components/RuntimeProvider';
import { ThemeProviderConfigurable } from '~/components/ThemeProviderConfigurable/ThemeProviderConfigurable';
import { cubeApi } from '~/services/cube';
import { fetchClient } from '~/services/http';

import { oidcConfig, snackbarConfig, curiosityContext, emotionCache, fileStorageContext } from './AppProviders.config';

LicenseInfo.setLicenseKey(appConfig.mui.license);

if (appConfig.agGrid.license) {
  LicenseManager.setLicenseKey(appConfig.agGrid.license);
}

const agGridModules = [AllEnterpriseModule];

const composeAny: any = compose;

export const AppProviders = (props: PropsWithChildren) => {
  const { isDev, hubble } = appConfig;
  const analytics = useMemo(
    () => getAnalyticsInstance('foundation-app', { isDev, hubble }),
    // An environment type doesn't change during runtime, only the config does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hubble],
  );

  return composeAny(
    //
    (children: ReactNode) => <CacheProvider value={emotionCache}>{children}</CacheProvider>,
    (children: ReactNode) => <FetchClientProvider client={fetchClient}>{children}</FetchClientProvider>,
    (children: ReactNode) => <CuriosityProvider {...curiosityContext}>{children}</CuriosityProvider>,
    (children: ReactNode) => <FileStorageProvider {...fileStorageContext}>{children}</FileStorageProvider>,
    (children: ReactNode) => <ConfigProvider>{children}</ConfigProvider>,
    (children: ReactNode) => <ModulePageConfigProvider>{children}</ModulePageConfigProvider>,
    (children: ReactNode) => <I18nextProvider i18n={i18n}>{children}</I18nextProvider>,
    (children: ReactNode) => <ThemeProviderConfigurable>{children}</ThemeProviderConfigurable>,
    (children: ReactNode) => <SnackbarProvider {...snackbarConfig}>{children}</SnackbarProvider>,
    (children: ReactNode) => <AnalyticsProvider instance={analytics}>{children}</AnalyticsProvider>,
    (children: ReactNode) => <DndProvider backend={HTML5Backend}>{children}</DndProvider>,
    (children: ReactNode) => <AuthProvider {...oidcConfig}>{children}</AuthProvider>,
    (children: ReactNode) => <ApolloAuthProvider>{children}</ApolloAuthProvider>,
    (children: ReactNode) => <CubeProvider cubeApi={cubeApi}>{children}</CubeProvider>,
    (children: ReactNode) => <FeatureFlagProvider>{children}</FeatureFlagProvider>,
    (children: ReactNode) => <PermissionsProvider>{children}</PermissionsProvider>,
    (children: ReactNode) => <Router preserve={preserveLocationSearchParams}>{children}</Router>,
    (children: ReactNode) => <RuntimeProvider>{children}</RuntimeProvider>,
    (children: ReactNode) => <IntercomProviderConfigurable>{children}</IntercomProviderConfigurable>,
    (children: ReactNode) => <ConfirmationDialogProvider>{children}</ConfirmationDialogProvider>,

    (children: ReactNode) => <PrintProvider>{children}</PrintProvider>,
    (children: ReactNode) => <AgGridProvider modules={agGridModules}>{children}</AgGridProvider>,
  )(props.children);
};
